import { Copy, Facebook, Linkedin, Mail, Share2, Twitter } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { Button } from "../../../ui/button";

export const SharePopup = ({ url, title }) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef(null);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: "bg-[#1DA1F2] hover:bg-[#1DA1F2]/90",
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "bg-[#4267B2] hover:bg-[#4267B2]/90",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "bg-[#0077b5] hover:bg-[#0077b5]/90",
    },
    {
      name: "Email",
      icon: Mail,
      url: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      color: "bg-gray-600 hover:bg-gray-700",
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "The article link has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again or copy the URL manually.",
        variant: "destructive",
      });
    }
  };

  // Optional: close popup when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      {/* Button that opens popup */}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Share this article"
        className="hover:bg-primary/10 hover:text-primary transition-all duration-300"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Share2 className="h-4 w-4" />
      </Button>
      {/* Popup content */}
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute right-0 bottom-full w-80 p-6 bg-white/98 backdrop-blur-md border border-gray-200/60 shadow-2xl rounded-xl mb-2 transition-all animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="font-bold text-lg text-gray-900">
                Share this article
              </h3>
              <p className="text-sm text-gray-600 mt-1.5">
                Help others discover this content
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {shareLinks.map((link) => (
                <Button
                  key={link.name}
                  variant="ghost"
                  size="sm"
                  className={`${link.color} text-white hover:scale-105 transition-all duration-300 shadow-md hover:shadow-lg rounded-lg font-medium`}
                  onClick={() => window.open(link.url, "_blank")}
                  aria-label={`Share on ${link.name}`}
                >
                  <link.icon className="h-4 w-4 mr-2" />
                  {link.name}
                </Button>
              ))}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="border border-gray-200 rounded-lg px-4 py-3 text-sm truncate flex-1 bg-gray-50/80">
                  <span className="text-gray-500 font-medium">Link:</span>{" "}
                  <span className="text-gray-700">{url.slice(0, 30)}...</span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyToClipboard}
                  aria-label="Copy link"
                  className="hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 rounded-lg"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
